import { differenceInSeconds, format, startOfWeek } from "date-fns"
import dayjs from "dayjs"
import { Constants } from "../constants"
import { ToDate } from "./toDate"

export const DifferenceInDate = (startDate?: Date | null, endDate?: Date | null) => {
  let differenceCalc: string | null = null
  let differences = endDate && startDate ? differenceInSeconds(new Date(endDate), new Date(startDate)) : null

  if (differences) {
    const daysCalc = differences / 86400
    let hourCal = ((daysCalc % 1) * 86400) / 3600
    let minCalc = ((hourCal % 1) * 3600) / 60
    let days = Math.floor(daysCalc)
    let hour = Math.floor(hourCal)
    let min = Math.floor(minCalc)
    let sec = Math.floor((minCalc % 1) * 60)

    differenceCalc = `${days > 0 ? `${days}d ` : ''}${hour > 0 ? `${hour}h ` : ''}${min > 0 ? `${min}m ` : ''}${sec > 0 ? `${sec}s` : ''}`
  }

  return differenceCalc
}

export function toUnitTimeZone(
  date: Date,
  offset: number = -6,
) {
  // Create a new Date object from the original date
  const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);

  // Apply offset (in minutes) and return new date
  const adjustedDate = new Date(utcDate.getTime() + offset * 60 * 60000);

  return adjustedDate;
}

export function calculateDateWithOffset(date: string | Date, offsetMinutes: number): Date {
  // Convert the input into a Date object
  const originalDate = typeof date === "string" ? new Date(date) : date;

  if (isNaN(originalDate.getTime())) {
    throw new Error("Invalid date");
  }

  // Calculate the new date by adding the offset in milliseconds
  const newDate = new Date(originalDate.getTime() + offsetMinutes * 60 * 1000);

  return newDate;
}

/**
 * Formatea un datetime aplicando timezone (trata entrada como UTC).
 * @param value - ISO string a formatear.
 * @param timezone - IANA timezone destino.
 * @param fmt - Formato dayjs opcional (default: ISO-like).
 * @returns String formateado o "-" si es nulo.
 */
export const fmtDateTimeWithTz = (value: string | null, timezone: string, fmt?: string): string => {
  if (!value) return '-'
  const utcDate = `${value.substring(0, 19)}Z`
  const resolved = fmt ?? 'YYYY-MM-DDTHH:mm:ss'
  return dayjs(utcDate).tz(timezone).locale('es').format(resolved)
}

/**
 * Genera rango de fechas en UTC para filtros de Elasticsearch.
 * @param params - Objeto con: from (inicio), to (fin), intervale (opcional).
 * @returns Objeto con gte y lte ajustados al offset UTC.
 */
export const buildUTCOffsetDateRange = ({ from, to, intervale }: {
  from: string | Date
  to: string | Date
  intervale?: string
}): { gte: string; lte: string } => {

  const UTC_OFFSET = Math.abs(Constants.defaultTimeZoneObject.value)

  const lte = format(dayjs(to).hour(23).minute(59).add(UTC_OFFSET, 'hour').toDate(), Constants.dateEndpoint2)

  if (intervale === '1w') {
    const gte = format(startOfWeek(ToDate(from), { weekStartsOn: 1 }), Constants.dateEndpoint2)
    return { gte, lte }
  }

  const gte = format(dayjs(from).add(UTC_OFFSET, 'hour').toDate(), Constants.dateEndpoint2)
  return { gte, lte }
}

/**
 * Unifica fecha y hora del backend aplicando timezone.
 * @param date - Fecha en formato "DD/MM/YYYY".
 * @param time - Hora en formato "HH:mm:ss" (ignora offset).
 * @param timezone - IANA timezone (ej. "America/Mexico_City").
 * @returns Objeto con fecha y hora formateadas o "N/A" si es inválido.
 */
export const parseTimeToTimeZone = (date: string, time: string, timezone: string): { date: string; hour: string } => {
  const fallback = { date: 'N/A', hour: 'N/A' }

  if (!date || !time || typeof date !== 'string' || typeof time !== 'string') return fallback

  try {
    const [day, month, year] = date.split('/')
    if (!day || !month || !year) return fallback

    const isoDatetime = `${year}-${month}-${day}T${time.substring(0, 8)}`
    const formatted = fmtDateTimeWithTz(isoDatetime, timezone, 'DD/MM/YYYY|hh:mm:ss A')

    const [datePart, hourPart] = formatted?.split('|') ?? []

    if (!datePart || !hourPart) return fallback

    return { date: datePart, hour: hourPart }

  } catch {
    return fallback
  }
}
